// Per-client proposal config for Nature's Nest Goa.
// This is a speculative working draft — internal R&D before the brief returns.
// When the brief lands, refine sections marked with [REFINE].

export const naturesNestProposal = {
  // ─── Identity ──────────────────────────────────────────────────
  slug: "natures-nest",
  client: {
    name: "Nature's Nest Goa",
    location: "Goa, Western Ghats",
    sector: "Eco-resort hospitality"
  },
  meta: {
    projectTitle: "A new home for Nature's Nest, online.",
    docCode: "SN-PRO-NN-001",
    preparedBy: "Sutranode",
    preparedFor: "The Nature's Nest team",
    preparedDate: "May 2026",
    validityDays: 30
  },

  // ─── The honest framing line, top of page ──────────────────────
  framing:
    "This is what we'd build for you based on what we already understand about your world. Once your brief reaches us, we'll refine. Consider this our first move, not our last word.",

  // ─── What we observed (speculative — refine post-brief) ────────
  observed: {
    eyebrow: "What we observed",
    // [REFINE] post-brief — these are research-flavored guesses framed in our voice.
    paragraphs: [
      "Nature's Nest sits inside the Mhadei wildlife corridor, where Malabar pied hornbills nest and the forest still holds its old quiet. Most guests find you through travel blogs and word-of-mouth. The site, today, doesn't carry that weight.",
      "Your competitive set in North Goa is loud — beach resorts, party stays, neon-lit booking flows. You're the opposite of all of that, and your website should be too. Quiet sells the kind of stay you offer.",
      "The booking decision for a place like yours happens slowly. Someone reads about the property on a Tuesday, comes back on Saturday, shows their partner on Sunday. The site has to survive being open in three different tabs over a week. That's a different design problem than a flash-sale resort site."
    ]
  },

  // ─── What we believe (point of view) ───────────────────────────
  belief: {
    eyebrow: "What we believe",
    headline: "Your current site sells rooms. The next one needs to sell the feeling of being there.",
    body:
      "An eco-resort website is a mood document, not a brochure. The job isn't to list amenities — it's to make a first-time visitor feel the cool of the forest at dawn, then make booking the obvious next step. Every design choice we recommend below ladders back to that one job."
  },

  // ─── The three paths intro ─────────────────────────────────────
  pathsIntro: {
    eyebrow: "The three paths",
    headline: "Named for the birds that live here.",
    body:
      "Three engagement shapes, scaled to ambition. Each is a complete website. The difference is how far we go beyond the site itself — into photography, post-launch, and the layer that turns a website into a quiet competitive moat."
  },

  // ─── TIERS ─────────────────────────────────────────────────────
  // Pricing structure is deliberate:
  //   Thrush ₹3.4L = anchor low. Exists so Flycatcher feels like the smart middle.
  //   Flycatcher ₹4.9L = the engineered choice. Carries one unexpected gift.
  //   Hornbill ₹7.4L = stretch. Its presence makes ₹4.9L feel like a deal.
  tiers: [
    {
      id: "thrush",
      bird: "Malabar Whistling Thrush",
      birdSub: "the whistling schoolboy",
      birdNote:
        "A common forest bird with a melodic call. Modest, dependable, present.",
      tagline: "A modernized site that doesn't embarrass you.",
      price: 340000,
      priceLabel: "₹3,40,000",
      priceJustification: "≈ 9 direct bookings at peak nightly rate.",
      timeline: "10–12 weeks",
      bestFor: "If the site is the only problem, and the rest of the marketing engine works.",
      includes: [
        "Strategy & sitemap (1 round)",
        "Design system + 6 core templates",
        "Build on Next.js + headless CMS",
        "Content migration (existing copy)",
        "Launch + 14-day stabilisation"
      ],
      excludes: [
        "Photography direction",
        "Post-launch optimisation beyond 14 days",
        "Booking-engine deep integration"
      ],
      twelveMonthOutcome: "A site that no longer looks dated. Same conversion shape.",
      cta: "Start with Thrush"
    },
    {
      id: "flycatcher",
      bird: "Nilgiri Flycatcher",
      birdSub: "the endemic blue",
      birdNote:
        "Found only in the Western Ghats. Elegant, deliberate, often the photograph guests take home.",
      tagline: "A site that compounds your direct bookings month over month.",
      price: 490000,
      priceLabel: "₹4,90,000",
      priceJustification: "≈ 14 direct bookings at peak nightly rate.",
      timeline: "14–16 weeks",
      bestFor: "If the site needs to do real work — convert browsers into stays, and grow with you.",
      mostChosen: true,
      includes: [
        "Everything in Thrush, plus:",
        "Brand voice + on-site copy refinement",
        "Story page + stay-detail layouts (premium)",
        "Booking-engine integration (your existing PMS)",
        "Pre-launch SEO foundation",
        "60-day post-launch optimisation window",
        // The unexpected gift — reciprocity. Don't move this to Hornbill.
        "Plus: a one-time Goa-season audit before peak booking months"
      ],
      excludes: [
        "Original photography direction",
        "Long-form post-launch growth retainer"
      ],
      twelveMonthOutcome:
        "Direct bookings as a share of total bookings rises. The OTAs become a top-of-funnel, not the funnel.",
      cta: "Build with Flycatcher"
    },
    {
      id: "hornbill",
      bird: "Great Indian Hornbill",
      birdSub: "the canopy giant",
      birdNote:
        "The bird every birder in the Western Ghats wants to see. Unmistakable, confident, impossible to ignore.",
      tagline: "A digital presence that becomes a reason guests choose you.",
      price: 740000,
      priceLabel: "₹7,40,000",
      priceJustification: "≈ 21 direct bookings at peak nightly rate.",
      timeline: "20–24 weeks",
      bestFor: "If the website is a strategic asset, not a line item — and you're building for the next 3–5 years.",
      flagship: true,
      includes: [
        "Everything in Flycatcher, plus:",
        "On-property photography + art-direction day in Goa",
        "Custom illustration set (forest motif, used sitewide)",
        "Editorial journal: a publishable home for stories from the property",
        "Interactive stay-planner (3-night vs 1-night nudge)",
        "6-month post-launch growth retainer (tuning + experimentation)",
        "Performance SLA + uptime monitoring"
      ],
      excludes: [
        "Paid media spend (we plan it; the spend is yours)"
      ],
      twelveMonthOutcome:
        "The site is referenced. Other resorts in the region quietly want what you have.",
      cta: "Partner on Hornbill"
    }
  ],

  // ─── Why we recommend Flycatcher (the honest pull) ─────────────
  recommendation: {
    eyebrow: "Our recommendation",
    headline: "For Nature's Nest, we'd build Flycatcher.",
    body: [
      "Thrush is fine if the only problem is that the site looks tired. We don't think that's your only problem — we think the site is leaving direct bookings on the table for OTAs, and a refresh alone won't fix that.",
      "Hornbill is the right scope if you're treating the website as a strategic asset for the next three years and you have the appetite for photography and a growth retainer right now. If that's true, we'd take it gladly. For most properties at your stage, though, it's more than is needed in year one.",
      "Flycatcher is the version where the work pays for itself. The booking-engine integration alone, done well, recovers the difference from Thrush within a season. The Goa-season audit before peak months is the kind of detail that compounds. It's the recommendation we'd make even if we weren't quoting it."
    ]
  },

  // ─── How we'd work ─────────────────────────────────────────────
  process: {
    eyebrow: "How we'd work together",
    headline: "Calm, sequenced, never improvised.",
    phases: [
      {
        n: "01",
        title: "Discovery",
        weeks: "Weeks 1–2",
        we: "Audit the existing site. Interview your team. Map the booking journey end-to-end.",
        you: "Two 90-minute conversations. Access to analytics + booking data."
      },
      {
        n: "02",
        title: "Design",
        weeks: "Weeks 3–7",
        we: "Design system, key templates, motion language. One round of revisions, scheduled.",
        you: "One review session per template milestone. Decisions, not opinions."
      },
      {
        n: "03",
        title: "Build",
        weeks: "Weeks 8–13",
        we: "Next.js + CMS implementation. Booking-engine integration. Performance budget enforced.",
        you: "Asset handover (logos, existing imagery). Booking-engine credentials."
      },
      {
        n: "04",
        title: "Launch",
        weeks: "Week 14",
        we: "DNS cutover on a quiet Tuesday morning. 72-hour war-room window.",
        you: "Sign-off. Coffee."
      },
      {
        n: "05",
        title: "Tune",
        weeks: "Weeks 15–22",
        we: "60-day optimisation. Heatmaps, A/B nudges, copy refinements. Goa-season audit before peak.",
        you: "Monthly 30-minute check-ins."
      }
    ]
  },

  // ─── Tech recommendation ───────────────────────────────────────
  tech: {
    eyebrow: "On the platform",
    headline: "We'd move you off WordPress.",
    body: [
      "Your current stack — WordPress + Elementor — optimises for the first month after launch. After that, every plugin update is a small risk and every page edit takes longer than it should.",
      "We'd rebuild on Next.js with a headless CMS (Sanity). Plain-English consequences: pages load fast on patchy Goa connectivity, your team can edit content without a developer, and we can extend the site over three years without it becoming brittle.",
      "The honest trade-off: you can't hire a random WordPress freelancer for one-off changes. Updates go through a developer or through the CMS we set up. We think that's the right trade-off for a property at your stage. If it's not, we should talk about that before we sign anything."
    ]
  },

  // ─── Proof — pulled from existing case studies ─────────────────
  proof: {
    eyebrow: "Work that's structurally similar",
    note: "We pick proof by problem-shape, not industry tag. These are the engagements closest in shape to yours."
  },

  // ─── Decision mechanics ────────────────────────────────────────
  next: {
    eyebrow: "What's next",
    headline: "Three ways to take the next step.",
    primary: {
      label: "Let's get on a call",
      // Replace with real Cal.com/Calendly link when ready.
      href: "https://cal.com/sutranode/proposal-natures-nest"
    },
    secondary: {
      label: "I'm ready — let's start",
      action: "intent"
    },
    tertiary: {
      label: "Reply to the email this came in",
      action: "noop"
    }
  },

  // ─── Notes & sources (the quiet labor-illusion footer) ─────────
  notesAndSources: {
    eyebrow: "Notes & sources",
    note: "What we read before writing this.",
    items: [
      "Your existing site at naturesnestgoa.com — every page, twice.",
      "Goa Tourism eco-stay benchmarks (peer set: 6 properties).",
      "OTA listings for Nature's Nest across Booking.com, Agoda, MakeMyTrip — to see the imagery and copy that represents you when you don't control the surface.",
      "Recent guest reviews — to find the words guests use about the property in their own voice.",
      "Mhadei wildlife corridor literature — for accuracy on the bird metaphors and on the ecological context the property sits in.",
      "Performance audits of three competitor sites on 4G mobile — to set our performance budget."
    ]
  },

  // ─── Validity ──────────────────────────────────────────────────
  validity: {
    line: "This proposal is valid for 30 days from the prepared date. The numbers, scope, and timeline are honest at this moment; if circumstances change before signing, we'll talk about it.",
    signature: "— Sachin, Sutranode"
  }
};
